import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { vehicleLabel } from "@/lib/vehicles";

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const EMPTY = { vehicle_id: "", date: "", service_name: "", description: "", parts: "", amount_tl: "", invoice_no: "", notes: "" };

export function ExternalServiceDialog({ open, onOpenChange, record, defaultVehicleId, onSaved }) {
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
    setForm(record
      ? { ...EMPTY, ...record, amount_tl: record.amount_tl ?? "" }
      : { ...EMPTY, date: today(), vehicle_id: defaultVehicleId || "" });
  }, [open, record, defaultVehicleId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.vehicle_id || !form.date || !form.service_name.trim()) {
      return toast.error("Araç, tarih ve servis adı zorunlu");
    }
    setSaving(true);
    try {
      const payload = { ...form, amount_tl: form.amount_tl === "" ? null : Number(form.amount_tl) };
      const r = record
        ? await api.put(`/external-services/${record.id}`, payload)
        : await api.post("/external-services", payload);
      toast.success(record ? "Dış servis kaydı güncellendi" : "Dış servis kaydı eklendi");
      onOpenChange(false);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail ? formatApiErrorDetail(e.response?.data?.detail) : "Hata");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="external-service-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{record ? "Dış Servis Kaydını Düzenle" : "Dış Servis Kaydı"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-1">
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider">Araç *</Label>
            <select value={form.vehicle_id} onChange={set("vehicle_id")} data-testid="es-vehicle-select"
              className="mt-1 w-full h-10 px-2 rounded-md border border-slate-200 bg-white text-sm font-mono">
              <option value="">— Araç seçin —</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{vehicleLabel(v.code, v.plate, v.brand)}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Tarih *</Label>
            <Input type="date" value={form.date} onChange={set("date")} className="mt-1" data-testid="es-date-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Servis Adı *</Label>
            <Input value={form.service_name} onChange={set("service_name")} className="mt-1" placeholder="Örn: Bursa Oto Servis" data-testid="es-service-name-input" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider">Yapılan İşlem</Label>
            <Textarea value={form.description} onChange={set("description")} className="mt-1" rows={3} data-testid="es-description-input" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider">Değişen Parçalar</Label>
            <Input value={form.parts} onChange={set("parts")} className="mt-1" data-testid="es-parts-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Tutar (TL)</Label>
            <Input type="number" min="0" step="0.01" value={form.amount_tl} onChange={set("amount_tl")} className="mt-1" data-testid="es-amount-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Fatura / İş Emri No</Label>
            <Input value={form.invoice_no} onChange={set("invoice_no")} className="mt-1" data-testid="es-invoice-input" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider">Not</Label>
            <Input value={form.notes} onChange={set("notes")} className="mt-1" data-testid="es-notes-input" />
          </div>
          <Button onClick={save} disabled={saving} className="sm:col-span-2 bg-[#1E3A8A] hover:bg-[#1e40af]" data-testid="es-save-btn">
            {saving ? "Kaydediliyor…" : record ? "Güncelle" : "Kaydet"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const fmtTL = (v) => (v === null || v === undefined || v === "" ? "—" : `₺${Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export function ExternalServiceCard({ r, onEdit, onDelete }) {
  return (
    <div className="border-2 border-orange-400 rounded-lg p-4 bg-orange-500/10" data-testid={`es-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-orange-700 font-mono">Dış Servis • {r.date}</div>
          <div className="font-heading font-bold text-slate-900 mt-0.5">{r.service_name}</div>
          <div className="text-xs text-slate-500 font-mono">{vehicleLabel(r.vehicle_code, r.vehicle_plate, r.vehicle_brand)}</div>
        </div>
        <div className="text-right">
          <div className="metric-num font-bold text-orange-700" data-testid={`es-amount-${r.id}`}>{fmtTL(r.amount_tl)}</div>
          <div className="flex gap-1 justify-end mt-1">
            {onEdit && <Button size="sm" variant="outline" className="h-7" onClick={() => onEdit(r)} data-testid={`es-edit-${r.id}`}>Düzenle</Button>}
            {onDelete && <Button size="sm" variant="outline" className="h-7 text-red-600" onClick={() => onDelete(r)} data-testid={`es-delete-${r.id}`}>Sil</Button>}
          </div>
        </div>
      </div>
      {r.description && <div className="text-sm text-slate-700 mt-2 whitespace-pre-line">{r.description}</div>}
      {(r.parts || r.invoice_no || r.notes) && (
        <div className="text-xs text-slate-500 mt-2 space-y-0.5">
          {r.parts && <div>Parçalar: {r.parts}</div>}
          {r.invoice_no && <div>Fatura No: {r.invoice_no}</div>}
          {r.notes && <div>Not: {r.notes}</div>}
        </div>
      )}
    </div>
  );
}
